/** A package in one catalogue sub-category, in one display currency. */
export interface QuantityPrice {
    qty: number;
    price: number;
}

export interface PriceComparison {
    referencePrice: number;
    savedAmount: number;
    discountPercent: number;
    referenceQty: number;
}

const cents = (amount: number) => Math.round(amount * 100);
const valid = (pkg: QuantityPrice) => Number.isInteger(pkg.qty) && pkg.qty > 0
    && Number.isFinite(pkg.price) && pkg.price > 0;

function compareWithAnchor(
    packages: readonly QuantityPrice[],
    selected: QuantityPrice,
    anchorQty: number,
): PriceComparison | null {
    if (!valid(selected)) return null;
    // Duplicate quantities can exist in an admin catalogue. The cheapest
    // purchasable reference gives the smallest, most conservative saving.
    const anchor = packages.filter(pkg => valid(pkg) && pkg.qty === anchorQty)
        .reduce<QuantityPrice | null>((best, pkg) => !best || pkg.price < best.price ? pkg : best, null);
    if (!anchor) return null;

    const referenceCents = Math.round(cents(anchor.price) * selected.qty / anchor.qty);
    const savedCents = referenceCents - cents(selected.price);
    if (savedCents <= 0) return null;
    const discountPercent = Math.round(savedCents / referenceCents * 100);
    return {
        referencePrice: referenceCents / 100,
        savedAmount: savedCents / 100,
        discountPercent,
        referenceQty: anchor.qty,
    };
}

/** Actual per-unit value against the smallest active package in this tier. */
export function packageUnitDiscount(
    packages: readonly QuantityPrice[],
    selected: QuantityPrice,
): PriceComparison | null {
    const smallestQty = packages.filter(valid).reduce((min, pkg) => Math.min(min, pkg.qty), Infinity);
    if (!Number.isFinite(smallestQty)) return null;
    const comparison = compareWithAnchor(packages, selected, smallestQty);
    return comparison && comparison.discountPercent > 0 ? comparison : null;
}

/** Savings at the real, same-tier 1,000-unit package rate; never an old-price claim. */
export function packageSavingsVsThousand(
    packages: readonly QuantityPrice[],
    selected: QuantityPrice,
): PriceComparison | null {
    return compareWithAnchor(packages, selected, 1000);
}

export interface CatalogPriceOffer {
    id: string;
    quantity: number;
    charge_price: number;
    charge_currency: string;
    category_slug: string;
    sub_category_slug?: string;
}

/** Checkout must verify both the selected row and its peer against live catalogue data. */
export function verifiedCatalogSavings(
    offers: readonly CatalogPriceOffer[],
    selection: { packageId: string; qty: number; price: number; currency: string },
): PriceComparison | null {
    const selected = offers.find(offer => offer.id === selection.packageId);
    if (!selected || selected.quantity !== selection.qty || selected.charge_currency !== selection.currency
        || !Number.isFinite(selected.charge_price) || Math.abs(selected.charge_price - selection.price) > 0.011) return null;
    const tier = offers.filter(offer => offer.category_slug === selected.category_slug
        && offer.sub_category_slug === selected.sub_category_slug
        && offer.charge_currency === selected.charge_currency);
    return packageSavingsVsThousand(
        tier.map(offer => ({ qty: offer.quantity, price: offer.charge_price })),
        { qty: selected.quantity, price: selected.charge_price },
    );
}

/** The undiscounted unit rate entered by the admin, before quantity multipliers. */
export function autoPackageReferencePrice(basePrice1000: number, qty: number): number {
    if (!Number.isFinite(basePrice1000) || basePrice1000 <= 0 || !Number.isInteger(qty) || qty <= 0) return 0;
    return Math.round(basePrice1000 * qty / 1000 * 100) / 100;
}
